import { useIsMutating } from "@tanstack/react-query";
import { useMemo } from "react";

import type { RXQuestionContent } from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model";
import { useInfiniteMessages } from "@/hooks/useInfiniteMessages";
import { useMedicationHistoryStore } from "@/store/medicationHistory";

/**
 * Whether this conversation's rx flow is the clinical-ai-v1-rx generalist one, which gets the new
 * transcript rendering. The backend stamps `generalist` onto the consent event it emits from the
 * generalist flow; the legacy runners emit the same event without it, so they render the old way.
 */
export function isGeneralistRxFlow(
  messages: ClientEventModel[] | undefined,
): boolean {
  const consents = (messages ?? []).filter((m) => m.type === "rx_flow_consent");
  const latest = consents[consents.length - 1];
  return !!(latest?.content as { generalist?: boolean } | undefined)
    ?.generalist;
}

/** True while awaiting the next rx card; false when the flow diverted (hide the loading skeleton). */
function awaitingNextRxCard(current: ClientEventModel[]): boolean {
  const last = current[current.length - 1];
  return (
    !!last &&
    (last.role === "user" ||
      last.type === "rx_input" ||
      last.type === "location_input")
  );
}

export type RxActiveQuestion = {
  id: string;
  content: RXQuestionContent;
  isLocation: boolean;
};

export type RxSurveyState = {
  /** The flow is running: consent issued and the questionnaire hasn't ended. */
  isActive: boolean;
  /** Consent granted upstream but no questions yet. Vestigial in clinical-ai-v1-rx (the flow opens
   * directly on the first question); the panel renders nothing in this state. */
  isAtConsent: boolean;
  /** The in-progress question (rendered in the panel, hidden from the transcript). */
  activeQuestion: RxActiveQuestion | null;
  activeQuestionId?: string;
  /** Message ids the rx panel owns, so the transcript suppresses them (the in-progress question).
   * Generic so adding more hidden messages never grows the transcript's props. */
  hiddenMessageIds: ReadonlySet<string>;
  /** Lock the chat composer while a question is on screen; open otherwise. */
  composerLocked: boolean;
};

const INACTIVE: RxSurveyState = {
  isActive: false,
  isAtConsent: false,
  activeQuestion: null,
  activeQuestionId: undefined,
  hiddenMessageIds: new Set(),
  composerLocked: false,
};

/**
 * Reconstructs the rx survey's state from the message stream — the single source of truth for the
 * panel (visibility + which card to show) and the conversation (which question to hide from the
 * transcript, and whether to lock the composer). The survey is server-driven, so there's no client
 * session to consult; everything derives from the persisted messages.
 */
export function useRxSurvey(
  conversationId: string | null | undefined,
): RxSurveyState {
  const { data } = useInfiniteMessages(conversationId ?? "");
  const messages = data?.allMessages;
  // While the Surescripts medication-history card is on screen for this conversation, the rx panel
  // pauses (and unlocks the composer) so the two cards don't stack — the survey resumes once the pull
  // card is done and the next question arrives.
  const medHistoryActive =
    useMedicationHistoryStore((s) => s.active)?.conversationId ===
    conversationId;
  // While a send for this conversation is still streaming, the next rx card is mid-flight in the same
  // turn. Keep the panel mounted on its skeleton rather than blanking it on a transient non-card
  // message that lands first (a streamed assistant line, a mid-turn refetch). Once the stream settles,
  // the message-derived divert check below decides: resume with the card, or pause for a real detour.
  const sendPending =
    useIsMutating({
      mutationKey: ["chat", "postMessage"],
      predicate: (m) =>
        (m.state.variables as { conversationId?: string } | undefined)
          ?.conversationId === conversationId,
    }) > 0;

  return useMemo(() => {
    if (medHistoryActive) return INACTIVE;
    const all = messages ?? [];
    // Everything derives from the current flow instance, which begins at the latest consent. A prior
    // questionnaire's messages (a decline's end event, earlier questions/answers) must not leak into a
    // restarted flow — otherwise the panel reads a stale state and shows an infinite skeleton.
    const latestConsentIndex = all.findLastIndex(
      (m) => m.type === "rx_flow_consent",
    );
    if (latestConsentIndex === -1) return INACTIVE;
    const current = all.slice(latestConsentIndex + 1);

    const ended = current.some((m) => m.type === "rx_questionnaire_end");
    if (ended) return INACTIVE;

    const isQuestion = (m: (typeof current)[number]) =>
      m.type === "rx_question" || m.type === "location_question";
    const isAnswer = (m: (typeof current)[number]) =>
      m.type === "rx_input" || m.type === "location_input";
    // "Answered" is any user turn after the question not just a card answer. A plain user message
    // still counts.
    const isUserTurn = (m: (typeof current)[number]) =>
      m.role === "user" || isAnswer(m);

    const questions = current.filter(isQuestion);

    // The active question is the latest one *only while it's unanswered*. Once its answer lands (before
    // the next question arrives) it must un-hide from the transcript, so the Q/A pair reads in order
    // rather than the answer appearing first and the question dropping in above it a moment later.
    const latestQuestionIndex = current.findLastIndex(isQuestion);
    const latest =
      latestQuestionIndex >= 0 ? current[latestQuestionIndex] : undefined;
    const latestAnswered = current
      .slice(latestQuestionIndex + 1)
      .some(isUserTurn);
    const content = latest?.content as RXQuestionContent | undefined;
    const hasValidContent =
      !!content &&
      typeof content === "object" &&
      (!!content.next_question || !!content.screening_complete);
    const activeQuestion: RxActiveQuestion | null =
      latest && hasValidContent && !latestAnswered
        ? {
            id: latest.id,
            content,
            isLocation: latest.type === "location_question",
          }
        : null;

    const isAtConsent = questions.length === 0;

    // The flow has diverted to a conversational detour — e.g. the photo gate's account-setup ask + the
    // verify card, which are plain assistant turns, not rx cards — when it's active with no rx card to
    // show, isn't mid-submit, and isn't mid-stream. Pause so the panel doesn't sit on a skeleton behind
    // the nudge; it resumes once the patient replies and the next rx card arrives. `sendPending` keeps
    // the skeleton up for the in-flight turn so a transient non-card message doesn't blank the panel.
    if (
      !activeQuestion &&
      !isAtConsent &&
      !awaitingNextRxCard(current) &&
      !sendPending
    ) {
      return INACTIVE;
    }

    return {
      // Past the guards above, the flow is running.
      isActive: true,
      isAtConsent,
      activeQuestion,
      activeQuestionId: activeQuestion?.id,
      // Messages the rx panel owns, so the transcript suppresses them: the in-progress question. A
      // generic set so adding more never grows NoriTurn's props.
      hiddenMessageIds: new Set(
        [activeQuestion?.id].filter(Boolean) as string[],
      ),
      // Lock while a question is on screen, since every question type is answered in the panel;
      // otherwise (no active question) leave the composer open.
      composerLocked: !!activeQuestion,
    };
  }, [messages, medHistoryActive, sendPending]);
}
